import React, {Component} from 'react';
import {connect} from 'react-redux';
import {DocumentTitle, Page, Icon, ValidatingInput} from '../widgets';
import UserType from '../user/UserType';
import FancyButton from '../widgets/FancyButton';
import Form from '../widgets/FormV2';
import {businessNameNotInPersonalName, nameStates} from './ProfileEditDetails';
import sessionActions from '../user/session.actions';
import {push} from 'connected-react-router';
import ProgressBar from '../widgets/ProgressBar';
import getGettingStartedProgress from './gettingStartedProgress';
import Dropzone from 'react-dropzone';
import Avatar from '../widgets/Avatar';
import {postFile} from '../fetchJson';
import uuidv4 from 'uuid/v4';
import handleError from '../handleError';
import './GettingStarted.css';

const getSessionProvider = props => props && props.currentSession && props.currentSession.provider;
const getSessionUser = props => props && props.currentSession && props.currentSession.user;
const getStateFromSession = (sessionUser, sessionProvider) =>
	sessionUser && sessionProvider
		? {
				firstname: sessionUser.firstname,
				lastname: sessionUser.lastname,
				businessName: sessionUser.businessName,
				subscriptionLevel: sessionProvider.subscriptionLevel,
				profession: sessionProvider.profession,
				avatarImage: sessionProvider.profile.avatarImage,
				updatingAvatarImage: false,
				draggingOverAvatarImage: false
		  }
		: {
				firstname: null,
				lastname: null,
				businessName: null,
				profession: null,
				avatarImage: null,
				updatingAvatarImage: false,
				draggingOverAvatarImage: false,
				uploadProgress: null
		  };
const getDataFromState = state => ({
	user: {
		firstname: state.firstname,
		lastname: state.lastname,
		businessName: state.businessName
	},
	profile: {
		avatarImage: state.avatarImage
	},
	profession: state.profession,
	subscriptionLevel: state.subscriptionLevel
});

class GettingStarted extends Component {
	constructor(props) {
		super(props);

		this.state = getStateFromSession(getSessionUser(props), getSessionProvider(props));

		this.setFirstname = e => this.setState({firstname: e.target.value});
		this.setLastname = e => this.setState({lastname: e.target.value});
		this.setBusinessName = e => this.setState({businessName: e.target.value});
		this.setProfession = e => this.setState({profession: e.target.value});
		this.setUserType = userType =>
			this.setState({
				subscriptionLevel: userType === 'provider' ? 'free' : 'none'
			});
		this.setAvatarImage = val => this.setState({avatarImage: val});

		this.dragOverAvatar = () => this.setState({draggingOverAvatarImage: true});
		this.dragOutAvatar = () => this.setState({draggingOverAvatarImage: false});

		this.dropAvatarFile = acceptedFiles => {
			if (!acceptedFiles || !acceptedFiles[0]) return;

			const imageUuid = uuidv4();
			const provider = getSessionProvider(this.props);

			this.setState({
				updatingAvatarImage: acceptedFiles[0].preview
			});

			postFile(
				`/api/providers/${provider.uuid}/image/${imageUuid}?avatar=true`,
				{image: acceptedFiles[0]},
				prog => this.setState({uploadProgress: prog})
			)
				.then(() => {
					this.setState({
						avatarImage: imageUuid,
						updatingAvatarImage: null
					});
				})
				.catch(this.props.handleError);
		};

		this.next = () => this.props.next(getDataFromState(this.state));
	}

	componentWillUpdate(nextProps) {
		if (nextProps.currentSession !== this.props.currentSession) {
			this.setState(getStateFromSession(getSessionUser(nextProps), getSessionProvider(nextProps)));
		}
	}

	render() {
		if (!this.props.currentSession || !this.props.currentSession.user) return null;

		const progress = getGettingStartedProgress(
			getSessionUser(this.props),
			getSessionProvider(this.props)
		);
		const {
			firstname,
			lastname,
			businessName,
			profession,
			subscriptionLevel,
			avatarImage,
			updatingAvatarImage,
			draggingOverAvatarImage
		} = this.state;

		return (
			<DocumentTitle title="B2Me">
				<Page className="GettingStarted">
					<Form>
						<p>
							It's great to have you on board. To get started, we need to know how you're going to
							use B2Me.
						</p>
						<h3>User type</h3>
						<UserType
							checked={subscriptionLevel !== 'none' ? 'provider' : 'user'}
							onChange={this.setUserType}
						/>
						<h3>Profile picture</h3>
						<p>Add a photo of yourself to help others on the site to get to know you!</p>
						<Dropzone
							className={`GettingStarted-avatar-dropzone ${draggingOverAvatarImage ? 'over' : ''} ${
								updatingAvatarImage ? 'updating' : ''
							}`}
							onDrop={this.dropAvatarFile}
							accept="image/jpeg, image/png"
							onDragEnter={this.dragOverAvatar}
							onDragLeave={this.dragOutAvatar}>
							<Avatar
								title="Click to upload an image of yourself"
								backgroundImage={updatingAvatarImage ? null : avatarImage}
								backgroundURL={updatingAvatarImage ? updatingAvatarImage : null}
								size={192}>
								<div />
							</Avatar>
						</Dropzone>
						<p>Enter your basic details so we can help to setup your profile:</p>
						<div>
							<label>
								<h3>First name</h3>
								<ValidatingInput
									maxLength={128}
									className="ProfileEditDetails-firstname"
									placeholder="What is your first name?"
									value={firstname}
									onChange={this.setFirstname}
									states={nameStates}
									state={
										lastname && lastname.length
											? businessNameNotInPersonalName(firstname, lastname, businessName)
												? 'valid'
												: 'businessName'
											: 'empty'
									}
									validIcon=""
								/>
							</label>
						</div>
						<div>
							<label>
								<h3>Last name</h3>
								<ValidatingInput
									maxLength={128}
									className="ProfileEditDetails-lastname"
									placeholder="What is your last name?"
									value={lastname}
									onChange={this.setLastname}
									states={nameStates}
									state={
										lastname && lastname.length
											? businessNameNotInPersonalName(firstname, lastname, businessName)
												? 'valid'
												: 'businessName'
											: 'empty'
									}
									validIcon=""
								/>
							</label>
						</div>
						{subscriptionLevel !== 'none' ? (
							<div>
								<label>
									<h3>Profession</h3>
									<input
										maxLength={64}
										className="ProfileEditDetails-profession"
										placeholder="List the services that you provide (e.g. Lettering | Wedding & Events | Hair & Makeup )"
										value={profession}
										onChange={this.setProfession}
									/>
								</label>
							</div>
						) : (
							undefined
						)}
						{subscriptionLevel !== 'none' ? (
							<div>
								<label>
									<h3>Business name</h3>
									<ValidatingInput
										maxLength={128}
										className="ProfileEditDetails-businessname"
										placeholder="What is the name of your business?"
										value={businessName}
										onChange={this.setBusinessName}
										states={nameStates}
										state={
											lastname && lastname.length
												? businessNameNotInPersonalName(firstname, lastname, businessName)
													? 'valid'
													: 'businessName'
												: 'empty'
										}
										validIcon=""
									/>
								</label>
							</div>
						) : (
							undefined
						)}
						<GettingStartedFooter
							progress={progress}
							showPrevious={false}
							subscriptionLevel={subscriptionLevel}
							next={this.next}
						/>
					</Form>
				</Page>
			</DocumentTitle>
		);
	}
}

export const GettingStartedFooter = ({
	progress,
	previous,
	next,
	showPrevious = true,
	nextLabel = 'Next',
	subscriptionLevel
}) => {
	return (
		<div className="FormV2-actions static">
			{subscriptionLevel !== 'none' ? (
				<div>
					<ProgressBar progress={progress} />
					<span style={{marginRight: 'auto'}}>{progress}%</span>
				</div>
			) : (
				undefined
			)}
			<div className="GettingStartedFooter-actions">
				{subscriptionLevel !== 'none' ? (
					showPrevious ? (
						<FancyButton onClick={previous}>Back</FancyButton>
					) : (
						undefined
					)
				) : (
					undefined
				)}
				<span>
					<FancyButton className="emphasis" onClick={next}>
						{nextLabel}
					</FancyButton>
					<button className="GettingStartedFooter-skip" onClick={next}>
						Skip
					</button>
				</span>
			</div>
		</div>
	);
};

const ConnectedGettingStarted = connect(
	({session}) => ({
		currentSession: session && session.current
	}),
	dispatch => ({
		next: data => {
			dispatch(sessionActions.sessionProfileFirstTime(true));
			dispatch(sessionActions.persistSessionField(data));
			dispatch(
				push(
					data.subscriptionLevel === 'none' ? '/getting-started-community' : '/getting-started-bio'
				)
			);
		},
		handleError: handleError(dispatch)
	})
)(GettingStarted);

export default ConnectedGettingStarted;



// WEBPACK FOOTER //
// ./src/profile/GettingStarted.js